import { zodResolver } from '@hookform/resolvers/zod';
import React, { useContext, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { AccountCreateDTO, AccountCreateDtoSchema, CallCenterDetailDTO } from '_lib/api/apiTypes.shim';

import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDetailsAccordion } from '_lib/components/ErrorDetailsAccordion/ErrorDetailsAccordion';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';

import { Stack } from '_lib/components/Stack/Stack';
import { useIsOverflowing } from '_lib/hooks/useIsOverflowing';

import { extractAllErrors } from '_lib/utils/helpers';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';

import { logDebug } from '_lib/utils/logging';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { AuthContext } from '../auth/authContext';
import { appRoutePaths, getFullAppRoutePath } from '../Nav/nav.types';

import { AccountForm } from './AccountForm';

export const AccountCreate = () => {
  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>();
  const [allErrors, setAllErrors] = useState<string[]>();

  const { enqueueSnackbar } = useOurSnackbar();

  const scrollTriggerTarget = useRef<HTMLDivElement>(null);
  const { isOverflowing } = useIsOverflowing(scrollTriggerTarget);

  const api = useContext(ApiContext);
  const { callCenterCacheResource } = useContext(AuthContext);

  const navigate = useNavigate();

  const accountPostMutation = api.accounts.useCreate();

  const cachedCallCenterQuery = callCenterCacheResource.useCacheQuery();

  const accountFormContext = useForm<AccountCreateDTO>({
    context: {
      callCenter: cachedCallCenterQuery.data
    },
    resolver: (values, context: { callCenter?: CallCenterDetailDTO } | undefined, options) => {
      const resolver = zodResolver(AccountCreateDtoSchema);
      return resolver(
        {
          ...values,
          callCenterId: context?.callCenter?.id || values.callCenterId
        },
        context,
        options
      );
    },
    defaultValues: {
      isCallTakingEnabled: false,
      isPortalEnabled: false
    },
    mode: 'onChange',
    shouldFocusError: true
  });

  const handleSave = apiSubmitHandler(
    accountFormContext.handleSubmit,
    accountFormContext.setError,
    async (data, { setFormErrors }) => {
      setNonFieldErrors(undefined);
      setAllErrors(undefined);
      try {
        const accountDetail = await accountPostMutation.create({ data, addRecordToCache: true });
        enqueueSnackbar('Account created');
        navigate(`${getFullAppRoutePath(appRoutePaths.Account)}/${accountDetail.id}`, { replace: true });
      } catch (err) {
        if (err instanceof BadRequestError) {
          setNonFieldErrors(err.responseData?.nonFieldErrors);
          setFormErrors(err);
        }
      }
    },
    {
      onInvalid: (errors) => {
        enqueueSnackbar('Validation Error occurred. Please check inputs and try again', { variant: 'error' });
        const allErrors = extractAllErrors(errors);
        setAllErrors(allErrors);
        logDebug(errors, allErrors);
      }
    }
  );

  return (
    <FlexBox
      sx={(theme) => ({
        backgroundColor: theme.palette.background.default,
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        position: 'relative'
      })}>
      <BackdropSpinner open={accountPostMutation.isPending} />
      <Stack padding={2} useFlexGap spacing={2} overflow='auto' ref={scrollTriggerTarget}>
        {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
        {!!allErrors?.length && <ErrorDetailsAccordion errors={allErrors} />}
        <AccountForm accountFormContext={accountFormContext} />
      </Stack>
      {isOverflowing && <Divider />}
      <FlexBox sx={{ paddingX: 2, paddingY: 1, marginLeft: 'auto' }}>
        <Button
          onClick={handleSave}
          disabled={accountPostMutation.isPending}
          text='Save'
          variant='contained'
          size='small'
          sx={{ textTransform: 'none' }}
        />
      </FlexBox>
    </FlexBox>
  );
};
